import { useEffect, useState } from "react";
import MobileSearchBar from "../../ui/MobileSearchBar";
import MobileList from "../../ui/MobileList";
import MobileListItem from "../../ui/MobileListItem";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileError from "../../ui/MobileError";
import { useGradableModelsSearch } from "./useGradableModelsSearch";
import {
  MobileCreateGradableModelSheet,
  MobileGradableModelDetailSheet,
} from "./MobileGradableModelSheets";

function modelSubtitle(model) {
  return [model?.game, model?.edition, model?.numbering].filter(Boolean).join(" · ");
}

export default function MobileGradableModelsList({ user }) {
  const { models, loading, error, filters, setFilters, search, refresh } =
    useGradableModelsSearch(user);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    setHasSearched(true);
    search();
  }, [search]);

  const handleSearch = (rawQ) => {
    const q = typeof rawQ === "string" ? rawQ : "";
    const next = { ...filters, q };
    setFilters(next);
    setHasSearched(true);
    search(next);
  };

  const openDetail = (model) => {
    setSelectedModel(model);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedModel(null);
  };

  const handleCreated = () => {
    setCreateOpen(false);
    setHasSearched(true);
    refresh();
  };

  const handleModelChanged = () => {
    closeDetail();
    refresh();
  };

  const searchLabel = filters.q?.trim() ? "Pesquisar" : "Pesquisar todos";

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-primary px-3 text-sm font-medium text-mgs-bg hover:bg-[#b47c06]"
        >
          Cadastrar
        </button>
        <button
          type="button"
          onClick={() => {
            setHasSearched(true);
            refresh();
          }}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-primary/50 px-3 text-sm font-medium text-laranjinha/80 hover:bg-monao"
        >
          Atualizar
        </button>
      </div>

      <MobileSearchBar
        value={filters.q}
        onChange={(q) => setFilters((current) => ({ ...current, q }))}
        onSubmit={handleSearch}
        submitLabel={searchLabel}
        placeholder="Nome, jogo, edição ou numeração"
      />

      {loading ? (
        <div aria-label="Carregando modelos" role="status">
          <MobileSkeleton lines={5} />
        </div>
      ) : error ? (
        <MobileError
          message={error.message ?? "Erro ao buscar modelos."}
          onRetry={refresh}
        />
      ) : hasSearched && models.length === 0 ? (
        <MobileEmpty
          title="Nenhum modelo encontrado"
          message={
            filters.q?.trim()
              ? `Nenhum resultado para “${filters.q.trim()}”.`
              : "Ajuste a pesquisa ou toque em Pesquisar."
          }
        />
      ) : models.length > 0 ? (
        <MobileList>
          {models.map((item) => (
            <MobileListItem
              key={item._id}
              title={item.name ?? "Sem nome"}
              subtitle={modelSubtitle(item)}
              onClick={() => openDetail(item)}
            />
          ))}
        </MobileList>
      ) : null}

      <MobileCreateGradableModelSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        user={user}
        onCreated={handleCreated}
      />

      <MobileGradableModelDetailSheet
        open={detailOpen}
        onClose={closeDetail}
        model={selectedModel}
        user={user}
        onChanged={handleModelChanged}
      />
    </div>
  );
}
